import { AccountingPeriod, Accumulated, TargetYear } from "@/types/enums";
import type { Dimension, TaxonomyTagType } from "@/types/mapping";

export enum CellType {
  HEADER = "HEADER",
  DATA = "DATA",
  SUM = "SUM",
  SECTION = "SECTION",
  NONE = "NONE",
}

export type RollForwardConfidence = "exact" | "review";

export type TaggedInfo = {
  key: string;
  type: string;
};

export type TaggedInfos = {
  roleKey: string | null;
  period: string | null;
  targetYear: string | null;
  accumulated: string | null;
  tags: TaggedInfo[];
};

export type CellInfo = {
  address: string;
  value: string;
  style: string;
  type: CellType;
  taggedInfos: TaggedInfos;
  // roll-forward 가 적용된 cell 의 LLM 확신도. None = forward 안 만진 cell.
  rollForwardConfidence?: RollForwardConfidence | null;
  // auto-tagging 으로 태깅된 cell 이면 true. 프론트는 이 플래그가 있는 cell 에만
  // auto-tag-info 엔드포인트를 호출한다 (lazy gating).
  isAutoTagged?: boolean;
};

export type RemoveTagFromCellsRequest = {
  roleKey: string;
  tagKey: string;
};

export type RemoveTagFromCellsResponse = {
  cellAddresses: string[];
};

export type GetCellsListResponse = {
  cells: CellInfo[];
  // 문단 셀이 표 너비로 가로 병합된 표인지. 병합 기능 이전에 분석된 표는 false 라,
  // 문단이 좁은 A열에 남아 있어 줄바꿈을 켜면 오히려 더 길게 감긴다.
  paragraphMerged: boolean;
};

export type TagParentInfo = {
  id: string;
  name: string;
  label: string;
  prefix: string;
  type: "AXIS" | "LINE_ITEMS";
};

export type CellMappingInfo = {
  accountingPeriod?: AccountingPeriod | null;
  targetYear?: TargetYear | null;
  accumulated?: Accumulated | null;
  tags?: TagInfo[];
};

export type TagInfo = {
  id: string;
  name: string;
  prefix: string;
  type: TaxonomyTagType;
  label: string;
  roleKey: string;
  parent: TagParentInfo | null;
};
export type GetTagsFromCellResponse = {
  tags: TagInfo[];
  period: AccountingPeriod | null;
  targetYear: TargetYear | null;
  accumulated: Accumulated | null;
};

/**
 * auto-tagging 이 한 셀에 대해 남긴 후보 한 건 (DB `auto_tag_info` 한 행).
 * tagging 컨텍스트의 `auto-tag-info` 엔드포인트가 flat 리스트로 내려준다.
 * MEMBER 는 `axisKey` 로 축을 구분(프론트가 group by), LINE_ITEM 은 `axisKey=null`.
 * rank 1 = AI 1순위, 2·3 = 대안. `proposalKoLabel` 이 있으면 미등록 신규 `entity_*` 제안.
 */
export type AutoTagCandidate = {
  tagType: "LINE_ITEM" | "MEMBER";
  rank: number;
  tagKey: string;
  axisKey: string | null;
  reason: string;
  extended: boolean;
  proposalKoLabel: string | null;
  proposalEnLabel: string | null;
};

export type GetCellAutoTagInfoResponse = {
  candidates: AutoTagCandidate[];
};
export enum MoneyUnit {
  KRW_1 = "KRW_1",
  KRW_1K = "KRW_1K",
  KRW_1M = "KRW_1M",
}
export type AssignCellRolesRequest = {
  cellAddresses: string[];
  definitionRoleKey: string;
};

export type AssignedCellInfo = {
  address: string;
  type: string;
  definitionRoleKey: string | null;
};

export type AssignCellRolesResponse = {
  cells: AssignedCellInfo[];
};

export type TableInfo = {
  id: string;
  name: string;
  roleKey: string;
  roleName: string;
  excelSheetId: string;
  address: string;
  accountSubject: string | null;
  annotation: string | null;
  footnote: string | null;
  moneyUnit: MoneyUnit | null;
  createdAt: Date;
  updatedAt: Date;
};

export type PatchSearchCacheRequest = {
  address: string;
  dimension: Dimension;
  extendedTag: {
    key: string;
  };
};

export type GuideDto = {
  title: string;
  description: string;
};

export type GetMappingGuideResponse = {
  guides: GuideDto[];
};

export type PatchUpdateCellInfoRequest = {
  type: CellType;
  definitionRoleKey: string | null;
};

export type DragTagToCellsRequest = {
  tagKey: string;
  dimension: Dimension;
  cellAddresses: string[];
  axisKey?: string | null;
};

export type DragTagToCellsResponse = {
  cells: Record<string, TaggedInfo[]>;
};

export type PeriodDragTaggingToCellsRequest = {
  cellAddresses: string[];
  period?: AccountingPeriod | null;
  targetYear?: TargetYear | null;
  accumulated?: Accumulated | null;
};

export type CellPeriodInfo = {
  period: AccountingPeriod | null;
  targetYear: TargetYear | null;
  accumulated: Accumulated | null;
};

export type PeriodDragTaggingToCellsResponse = {
  cells: Record<string, CellPeriodInfo>;
};

export type PatchTableNotUsedSumTagKeysRequest = {
  notUsedSumTagKeys: string[];
};

export type GetTableNotUsedSumTagKeysResponse = {
  notUsedSumTagKeys: string[];
};
